import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import postal from 'postal';
import { usePopper } from 'react-popper';

import Table from '../../ui/tables/Table';
import useTableNavigation from '../../ui/tables/useTableNavigation';

import styled from 'styled-components';

import useTableStorage from '../../hooks/useTableStorage';
import PrimaryButton from '../PrimaryButton';
import SecondaryButton from '../SecondaryButton';

import { TableColumn } from '../../tableSettings';
import { focusNextTabable } from '../../util/inputUtils';

export const LookupMaxRecords = 100;
export const GetAllRecords = 999999; // 갯수 제한 없이 쿼리해야하는 경우 사용

const LoopupPopup = styled.div`
  background-color: white;
  box-shadow: rgba(0, 0, 0, 0.19) 0px 10px 20px, rgba(0, 0, 0, 0.23) 0px 6px 6px;
  z-index: 999999999 !important;

  .lookup-footer {
    display: flex;
    padding-left: 5px;
    padding-right: 5px;
    padding-bottom: 5px;
  }

  .additional-search {
    float: left;
    width: 200px;
  }

  .commands-buttons {
    float: left;
    width: 100%;
  }

  .commands-buttons-left {
    float: left;
  }

  .commands-buttons-right {
    float: right;
  }
`;

const LookupContainer = styled.div`
  position: relative;
  width: 100%;

  .ifms-lookup-icon {
    position: absolute;
    right: 5px;
    font-size: 10px;
    top: 4px;
    color: #a1a1a1;
  }
`;

export enum LookupInputType {
  AllowInputValue,
  RollBackPrevValue,
  RollBackPrevValueAllowEmpty,
  ClearBlankValue,
}

interface LastQuery {
  text: string | null,
  more: string | null,
};

function LookupField({
  query,
  placeholder = '',
  width = undefined,
  columns,
  onSelect,
  onCancel,
  labelProp,
  initialValue,
  onTextChange,
  filter,
  disabled = false,
  lookupId = undefined,
  onTabNext = undefined,
  onTabPrevious = undefined,
  searchMatchOnTab = false,
  hasLookupValue = false,
  activator = false,
  onFocus = () => { },
  onBlur = () => { },
  triggerLookupOnTab = false,
  focusableWhenDisabled = false,
  validationFor = undefined,
  backgroundColor = undefined,
  maxLength = undefined,
  showEmptyTable = true,
  customContent,
  isMultiple = false,
  noBorder = false,
  tableHeight = undefined,
  focusElementAfterSelect = () => null,
  userInputType = 0,
  name = undefined,
  noRightBorder = false,
  noLeftBorder = false,
  notFocusNextEleWhenSelectItem = false,
  isLookupTable = true,
  isMultipleSelected = false,
  isMultiRptOption = false,
}: {
  query: (x: string | null) => Promise<Array<any>>,
  onSelect: (x: any) => void,
  onCancel: VoidFunction,
  labelProp: string,
  initialValue: string | null | undefined,
  placeholder: string,
  width: string | undefined,
  columns: Array<TableColumn>,
  disabled?: boolean,
  onTextChange: (text: string) => void,
  filter: (data: Array<any>, filterText: string) => Array<any>,
  lookupId?: string | undefined,
  onTabNext?: (() => boolean) | undefined,
  onTabPrevious?: (() => boolean) | undefined,
  hasLookupValue?: boolean,
  searchMatchOnTab: any | undefined,
  activator: any | undefined,
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void,
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void,
  triggerLookupOnTab?: boolean,
  focusableWhenDisabled?: boolean,
  validationFor?: string | undefined,
  backgroundColor?: string | undefined,
  maxLength?: number | undefined,
  showEmptyTable?: boolean,
  isMultiple?: boolean,
  noBorder?: boolean,
  customContent?: ReactNode,
  tableHeight?: string | undefined,
  focusElementAfterSelect?: () => HTMLInputElement | HTMLElement | null,
  userInputType?: LookupInputType,
  name?: string | undefined,
  noRightBorder?: boolean,
  noLeftBorder?: boolean,
  notFocusNextEleWhenSelectItem?: boolean,
  isLookupTable?:boolean,
  isMultipleSelected?: boolean,
  isMultiRptOption?: boolean,
}) {
  const channel = postal.channel('ifms');
  const tableStorage = useTableStorage();
  const autocompleteOffGuard = _.uniqueId('autocomplete-off-');

  const [initialized, setInitialized] = useState(false);
  const [customTableId] = useState(lookupId ? `${lookupId}-lookuptable` : _.uniqueId('ifms-lookuptable-'));
  const [inputId] = useState(!!lookupId ? lookupId : _.uniqueId('ifms-lookup-'));

  const [oldSelectedValue, setOldSelectedValue] = useState<string>(initialValue ?? '');

  const lastQueryRef = useRef<LastQuery>({ text: null, more: null });
  const queryQueue = useRef<string[]>([]);
  const isQueryRunning = useRef(false);

  const isFindingRef = useRef(false);
  const lastFindedValueRef = useRef('');
  const [findedData, _setFindedData] = useState<any[]>([]);
  const [data, _setData] = useState<any[]>([]);

  const findedDataRef = useRef<any[]>([]);
  const dataRef = useRef<any[]>([]);

  const [selectedIndex, _setSelectedIndex] = useState(-1);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const [showPopup, _setShowPopup] = useState(false);
  const [referenceElement, _setReferenceElement] = useState<HTMLInputElement | null>(null);
  const [popperElement, _setPopperElement] = useState<HTMLDivElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom-start',
    modifiers: [{ name: 'arrow', options: { element: arrowElement } }],
    strategy: 'fixed'
  });
  
  const eventChangeTrigger = [isMultipleSelected]

  const {
    selectTableCol,
    selectTableRow,
    tableNavigationCleanup
  } = useTableNavigation(customTableId, setSelectedIndex, handleSelect, [], true, true, false, true, undefined, initialized, false, eventChangeTrigger);
  
  const additionalSearchInput = useRef<HTMLInputElement>(null);
  const popperRef = useRef<HTMLDivElement | null>(null);
  const referenceRef = useRef<HTMLInputElement | null>(null);
  const selectedIndexRef = useRef(-1);

  const fieldClickRef = useRef(false);
  let openLookupSubscription: ISubscriptionDefinition<any> | null = null;

  function subscribeChannels() {
    openLookupSubscription = postal.subscribe({
      channel: 'ifms',
      topic: 'ifms.lookup-open',
      callback: (data) => onEnter(data.targetId)
    });
  }

  function unsubscribeChannels() {
    if (openLookupSubscription) {
      postal.unsubscribe(openLookupSubscription);
    }
  }

  function resetTableScrollAndSelect() {
    const tableBody = popperElement?.querySelector<HTMLDivElement>(`#${customTableId}`);
    if (!tableBody) return;

    tableBody.scrollTop = 0;
    requestAnimationFrame(() => {
      selectTableRow(0);
      selectTableCol(0);
      setSelectedIndex(0);
    });
  }

  function setFindedData(d: any) {
    if (!d) return;

    if (d.length > 0) {
      resetTableScrollAndSelect();
    }

    findedDataRef.current = d;
    _setFindedData(d);
  }

  function setData(d: any) {
    if (!d) return;

    if (d.length > 0) {
      resetTableScrollAndSelect();
    }

    if (additionalSearchInput && additionalSearchInput.current) {
      additionalSearchInput.current.value = '';
    }
    isFindingRef.current = false;
    lastFindedValueRef.current = '';
    setFindedData([]);

    dataRef.current = d;
    _setData(d);
  }

  function setSelectedIndex(i: number) {
    if (i !== selectedIndexRef.current) {
      selectedIndexRef.current = i;
      _setSelectedIndex(i);
    }
  }

  function setShowPopup(s: boolean) {
    if (!s) {
      tableNavigationCleanup();
    }
    _setShowPopup(s);
  }

  function setReferenceElement(e: HTMLInputElement | null) {
    referenceRef.current = e;
    _setReferenceElement(e);
  }

  function setPopperElement(e: HTMLDivElement | null) {
    popperRef.current = e;
    _setPopperElement(e);
  }

  useEffect(() => {
    subscribeChannels();
    document.addEventListener('click', handleClickOutside, true);

    return () => {
      unsubscribeChannels();
      document.removeEventListener('click', handleClickOutside, true);
      tableNavigationCleanup();
    };
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (!popperElement) return;

    if (showPopup) {
      selectTableRow(0);
      selectTableCol(0);
      setSelectedIndex(0);
      if (activator) {
        const table = popperElement?.querySelector<HTMLDivElement>(`#${customTableId}`);
        if (table) {
          table.focus();
        }
      }
    }
    else {
      lastQueryRef.current = { ...lastQueryRef.current, text: '', more: '' };
      selectTableRow(-1);
      selectTableCol(-1);
      setSelectedIndex(-1);
      setData([]);
    }
    setInitialized(showPopup);
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [popperElement, showPopup]
  );

  useEffect(() => {
    if (referenceElement && !activator) {
      referenceElement.value = initialValue ?? '';
    }
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialValue]
  );

  function shouldCallDB(nextText: string) {
    if (dataRef.current.length === 0) return true;
    if (dataRef.current.length >= LookupMaxRecords) return true;
    if (!lastQueryRef.current.text) return true;
    if (nextText.length < lastQueryRef.current.text.length) return true;
    if (!nextText.startsWith(lastQueryRef.current.text)) return true;
    return false;
  }

  function enqueueQuery(text: string) {
    if (queryQueue.current.includes(text)) return;

    queryQueue.current.push(text);
    runNextQuery();
  }

  async function runNextQuery() {
    if (isQueryRunning.current) return;

    const nextText = queryQueue.current.shift();
    if (!nextText) return;

    isQueryRunning.current = true;
    try {
      await executeQuery(nextText, shouldCallDB(nextText));
    } finally {
      isQueryRunning.current = false;
      runNextQuery();
    }
  }

  function inputValueChange(e: React.ChangeEvent<HTMLInputElement>) {
    const text = e.target.value;
    onTextChange(text);

    if (text.length === 0) {
      setShowPopup(false);
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      return;
    }

    if (text.length > 2 || showPopup) {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        enqueueQuery(text);
      }, 350);
    }
  }

  function processTabNext(e: React.KeyboardEvent<HTMLInputElement>) {
    if (additionalSearchInput.current) {
      additionalSearchInput.current?.focus();
      e.preventDefault();
      return;
    }
    if (onTabNext) {
      const tabProcessed = onTabNext();
      if (tabProcessed) {
        e.preventDefault();
        return;
      }
    }
  }

  function processTabPrevious(e: React.KeyboardEvent<HTMLInputElement>) {
    if (onTabPrevious) {
      const tabProcessed = onTabPrevious();
      if (tabProcessed) {
        e.preventDefault();
        return;
      }
    }
  }

  function handleClickOutside(e: MouseEvent) {
    const target = e.target as Element;
    if (!popperRef || !referenceRef || !popperRef.current || !referenceRef.current || target?.id === inputId) {
      if (showPopup) {
        setShowPopup(false);
      }
      return;
    }

    if (!popperRef.current.contains(target) && !referenceRef.current.contains(target)) {
      handleCancel();
      e.stopPropagation();
    }
  }

  async function onActivateExternal() {
    await executeQuery();
  }

  async function onEnterWithActivator() {
    if (showPopup) {
      handleSelect(selectedIndex);
    } else {
      await executeQuery();
    }
  }

  async function onEnter(targetId?: string) {
    if (!!activator) {
      await onEnterWithActivator();
      return;
    }

    if (!referenceRef.current || (targetId && referenceRef.current.id !== targetId)) {
      return;
    }

    if (showPopup) {
      handleSelect(selectedIndex);
    } else {
      await executeQuery(referenceRef.current.value);
    }
  }

  async function executeQuery(text: string | null = null, callDB: boolean = true) {
    if (callDB) {
      const queryResult = await query(text);
      if (!showEmptyTable && (!queryResult || !queryResult.length)) {
        return;
      }
      setData(queryResult);
    }
    else {
      const filteredData = filter(dataRef.current, text ?? '');
      setData(filteredData);
    }

    if (_.isEmpty(referenceElement?.value) && !_.isEmpty(text) && !activator)
      setShowPopup(false);
    else {
      lastQueryRef.current = { ...lastQueryRef.current, text };
      setShowPopup(true);
    }
  }

  function handleEscape(e: React.KeyboardEvent<HTMLInputElement>) {
    if (showPopup) {
      handleCancel();
      e.stopPropagation();
    }
  }

  async function doSearchMatchOnTab() {
    if (!referenceRef.current) {
      return;
    }

    const searchValue = referenceRef.current.value;
    if (!searchValue || searchValue === lastQueryRef.current.text) {
      return false;
    }

    const queryResult = await query(searchValue);
    if (!queryResult || queryResult.length <= 0) {
      return false;
    }

    const exactMatch = queryResult.find(x => x[labelProp] && x[labelProp].toLowerCase() === searchValue);
    if (exactMatch) {
      lastQueryRef.current = { ...lastQueryRef.current, text: exactMatch[labelProp] };
      onSelect(exactMatch);
      return false;
    } else {
      setData(queryResult);

      lastQueryRef.current = { ...lastQueryRef.current, text: searchValue };
      setShowPopup(true);

      if (referenceRef.current) {
        referenceRef.current.focus();
      }

      return true;
    }
  }

  function isEditTableCell(element: Element): boolean {
    const divElements = document.querySelectorAll('div[id$="-editabletable"]');
    return Array.from(divElements).some(div => div.contains(element));
  }

  async function executeLookupOnTab() {
    if (referenceRef.current && referenceRef.current.value) {
      // Let's try to query, if there is only single result with exact same value, select it. Otherwise, show lookup popup
      const queryResult = await query(referenceRef.current.value);

      if (queryResult && queryResult.length === 1 && queryResult[0][labelProp] && queryResult[0][labelProp].toLowerCase() === referenceRef.current.value) {
        onSelect(queryResult[0]);

        // Find next tabbable and focus it, since default event propagation needed to be stopped
        setTimeout(() => {
          if (referenceRef.current)
            focusNextTabable(referenceRef.current);
        }, 10)
        return;
      }
    }
    onEnter();
  }

  async function processSpecialKeys(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!e || !e.keyCode) {
      return;
    }

    // focusable when disabled fields are actually enabled text fields, change is not allowed
    if (disabled) {
      if (!(e.ctrlKey && (e.keyCode === 67 || e.keyCode === 65))) {
        e.stopPropagation();
        e.preventDefault();
        return;
      }
    }

    const tab = 9;

    if (e.keyCode === tab) {
      if (triggerLookupOnTab && !!referenceRef.current && !!referenceRef.current.value && !hasLookupValue) {
        e.stopPropagation();
        e.preventDefault();
        executeLookupOnTab();
        return;
      }
      if (searchMatchOnTab && !hasLookupValue) {
        const stopFurther = await doSearchMatchOnTab();
        if (stopFurther) {
          e.stopPropagation();
          e.preventDefault(); // Typing is not allowed
          return;
        }
      }

      if (e.shiftKey) {
        processTabPrevious(e);
      } else {
        processTabNext(e);
      }
      return;
    }

    switch (e.keyCode) {
      case 40: // Arrow down
        e.preventDefault();
        return;
      case 27:
        handleEscape(e);
        return;
      case 38: // Arrow up
        e.preventDefault();
        return;
      case 9: // Tab
        handleCancel();
        e.preventDefault();
        e.stopPropagation();
        return;
      case 13: // Enter
        onEnter();
        e.preventDefault();
        e.stopPropagation();
        return;
      default:
    }
  }

  function handleSelect(index: number) {
    const customTable = document.getElementById(customTableId);
    const isMultipleHeader = customTable?.querySelectorAll("[data-column-id='select']");
    const targetData = isFindingRef.current ? findedDataRef.current : dataRef.current;
    if (index < 0 || index > targetData.length - 1 || (isMultiRptOption && isMultipleSelected)) {
      handleCancel();
    } else {
      if ((isMultipleHeader && isMultipleHeader.length === 0) || (isMultiRptOption && isMultipleHeader && isMultipleHeader.length === 1)) {
        const referenceElementValue = targetData[index][labelProp] ? targetData[index][labelProp] : '';
        if (referenceRef.current) {
          referenceRef.current.value = referenceElementValue;
          onTextChange(referenceElementValue);
          setOldSelectedValue(referenceElementValue);
        }
        // 선택후에는 값을 지워주도록 수정. 다음번에 영향을 줌.
        //setLastQuery({...lastQuery, text: referenceElementValue});
        lastQueryRef.current = { ...lastQueryRef.current, text: '', more: '' };
        onSelect(targetData[index]);
        handleCancel();

        const focusElement = focusElementAfterSelect();
        if (focusElement) {
          if (focusElement instanceof HTMLInputElement)
            focusElement?.select();
          else if (focusElement instanceof HTMLElement)
            focusElement?.focus();
          else return;
        }
        else {
          //focus next tabable field after select
          setTimeout(() => {
            if (isEditTableCell(referenceRef.current as HTMLInputElement)) {
              channel.publish('ifms.editable-table-onEnter', { backFocus: false, changeFocus: true, element: referenceRef.current });
            }
            else {
              if (!notFocusNextEleWhenSelectItem) {
                if (referenceRef.current)
                  focusNextTabable(referenceRef.current)
              }
            }
          }, 10)
        }
      }
    }
  }

  function handleOk(selectedIndex: number) {
    if (!isMultiple) {
      handleSelect(selectedIndex)
    } else {
      setShowPopup(false);
    }
  }

  function handleCancel() {
    setShowPopup(false);
    if (additionalSearchInput && additionalSearchInput.current) {
      additionalSearchInput.current.value = '';
    }
    onCancel();
    if (referenceRef.current && !activator) {
      referenceRef.current.focus();
    }
  }

  function processFilterMoreSpecialKeys(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!e || !e.keyCode) {
      return;
    }

    switch (e.keyCode) {
      case 40: // Arrow down
        e.preventDefault();
        return;
      case 27:
        handleEscape(e);
        return;
      case 38: // Arrow up
        e.preventDefault();
        return;
      case 9: // Tab
        handleCancel();
        e.preventDefault();
        e.stopPropagation();
        return;
      case 13: // Enter
        if (additionalSearchInput && additionalSearchInput.current && additionalSearchInput.current.value !== lastFindedValueRef.current) {
          onFindMoreClick();
        } else {
          handleSelect(selectedIndex);
        }
        e.stopPropagation();  // Enter Key Event가 이후까지 영향을 주지 않도록 함.
        return;
      default:
    }
  }

  function onFindMoreClick() {
    const searchValue = (additionalSearchInput.current?.value ?? '').trim();
    removeAllHighlights();
    lastFindedValueRef.current = searchValue;

    if (!searchValue) {
      isFindingRef.current = false;
      setFindedData([]);
      findedDataRef.current = [];
    } else {
      const searchTerm = searchValue.toLowerCase();
      const accessors = tableProps.columns.map(c => c.accessor);

      const filteredData = data.filter(item =>
        accessors.some(a => {
          const v = item[a];
          return v && v.toString().toLowerCase().includes(searchTerm);
        })
      );

      isFindingRef.current = true;
      setFindedData(filteredData);
      findedDataRef.current = filteredData;
    }

    setTimeout(() => {
      const targetData = isFindingRef.current ? findedDataRef.current : dataRef.current;
      highlightSearchTermInCells(searchValue, targetData);
    }, 150);
  }

  function highlightSearchTermInCells(searchValue: string, targetData: any[]) {
    const searchTerm = searchValue.toLowerCase();
    const accessors = tableProps.columns.map(column => column.accessor);

    for (let rowIndex = 0; rowIndex < targetData.length; rowIndex++) {
      accessors.forEach(accessor => {
        const cellId = `${accessor}-${rowIndex}`;

        const cellElement = popperRef.current?.querySelector(`#${cellId}`);

        if (cellElement) {
          const cellText = cellElement.textContent;

          if (cellText && cellText.toLowerCase().includes(searchTerm)) {
            const originalText = cellElement.textContent ?? '';

            const highlightedHtml = highlightTextPortion(originalText, searchTerm);

            cellElement.innerHTML = highlightedHtml;
          }
        }
      });
    }
  }

  function highlightTextPortion(text: string, searchTerm: string) {
    const searchRegex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text.replace(searchRegex, '<span style="background-color: yellow;">$1</span>');
  }

  function removeAllHighlights() {
    const accessors = tableProps.columns.map(column => column.accessor);
    const targetData = isFindingRef.current ? findedDataRef.current : dataRef.current;

    for (let rowIndex = 0; rowIndex < targetData.length; rowIndex++) {
      accessors.forEach(accessor => {
        const cellId = `${accessor}-${rowIndex}`;
        const cellElement = document.getElementById(cellId);

        if (cellElement) {
          const originalText = cellElement.textContent;
          cellElement.innerHTML = originalText ?? "";
        }
      });
    }
  }

  function handleFocus(e: React.FocusEvent<HTMLInputElement>) {
    // Grid > Lookup을 동작할 때 창 내부 항목을 클릭하면 Lookup창이 닫히는 문제
    // if (showPopup) {
    //   setShowPopup(false);
    // }

    if (onFocus) {
      onFocus(e);
    }
  }

  function onClick(e: React.MouseEvent) {
    if (!disabled && !fieldClickRef.current) {
      (e.target as HTMLInputElement).select();
      fieldClickRef.current = true;
    }
  }

  function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
    if (popperRef.current) {
      if (!popperRef.current.contains(e.relatedTarget)) handleCancel();
      else return;
    }

    if (_.isEmpty(e.target.value))
      setOldSelectedValue('');

    switch (userInputType) {
      case LookupInputType.RollBackPrevValue:
        e.target.value = oldSelectedValue;
        break;
      case LookupInputType.RollBackPrevValueAllowEmpty:
        if (!_.isEmpty(e.target.value))
          e.target.value = oldSelectedValue;
        break;
      case LookupInputType.ClearBlankValue:
        e.target.value = '';
        break;
    }

    if (onBlur) {
      onBlur(e);
    }

    fieldClickRef.current = false;
  }

  let appliedPopperStyles = {
    ...styles.popper,
  };

  // if(isMultiple) {
  //   appliedPopperStyles.height = 250;
  // }

  if (!!width) {
    appliedPopperStyles = { ...appliedPopperStyles, width };
  }

  const tableProps = {
    customTableId,
    overflowX: 'hidden',
    overflowY: 'scroll',
    height: tableHeight ?? '250px',

    columns,
    data: isFindingRef.current ? findedData : data,
    selectable: true,
    alternateRows: true,

    // onRowSelected: setSelectedIndex,
    // onRowDoubleClick: handleSelect,
    selectedRowIndex: selectedIndex,
    useHighlightCellOnMouseover: false,
    isLookupTable
  };

  let classes = focusableWhenDisabled && disabled
    ? 'ifms-lookup e-ifms-input e-outline ifms-tabable e-ifms-focusable-when-disabled'
    : 'ifms-lookup e-ifms-input e-outline ifms-tabable';

  if (!disabled) {
    classes = `${classes} ifms-tabable-active`;
  }
  else {
    classes = `${classes} selenium-test-disabled`;
  }

  let inputStyle: React.CSSProperties = { paddingRight: '20px', backgroundColor };
  if (noBorder) {
    inputStyle.border = 'none';
  }
  if (noRightBorder) {
    inputStyle.borderRight = 'none';
  }
  if (noLeftBorder) {
    inputStyle.borderLeft = 'none';
  }

  return (
    <>
      {!!activator &&
        <>{activator(onActivateExternal, setReferenceElement)}</>
      }
      {!activator &&
        <LookupContainer>
          {!!customContent ? (
            <div ref={setReferenceElement} onClick={() => { onEnter(); }}>{customContent}</div>
          ) : (
            <>
              <input
                id={inputId}
                name={name}
                disabled={disabled && !focusableWhenDisabled}
                ref={setReferenceElement}
                type="text"
                className={classes}
                placeholder={placeholder}
                onKeyDown={processSpecialKeys}
                defaultValue={initialValue || ''}
                onChange={e => inputValueChange(e)}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onClick={onClick}
                style={inputStyle}
                //autoComplete={autocompleteOffGuard}
                autoComplete="off"
                list={autocompleteOffGuard}
                data-tooltip="-from-value"

                data-tooltip-onlywhencutoff
                data-validationfor={validationFor}
                maxLength={maxLength}
              />
              <FontAwesomeIcon color='black' icon={faSearch} className="ifms-lookup-icon" />
            </>
          )}
        </LookupContainer>
      }
      {showPopup &&
        <LoopupPopup ref={setPopperElement} style={appliedPopperStyles} {...attributes.popper}>
          <Table {...tableProps} />
          <div ref={setArrowElement} style={styles.arrow} />
          {//!isMultiple && 
            (
              <div className="lookup-footer">
                <div className="additional-search">
                  <input
                    ref={additionalSearchInput}
                    id='ifms-lookup-search'
                    type="text"
                    className="e-ifms-input e-outline ifms-tabable"
                    placeholder="Filter search list"
                    onKeyDown={processFilterMoreSpecialKeys}
                    autoComplete="off"
                    list="autocompleteOff"
                    data-tooltip="-from-value"

                    data-tooltip-onlywhencutoff
                  />
                </div>
                <div className="commands-buttons">
                  <div className="commands-buttons-left">
                    &nbsp;<PrimaryButton id="ifms-lookup-find" skipTableCheck size="small" onClick={onFindMoreClick}>Find</PrimaryButton>
                  </div>
                  <div className="commands-buttons-right">
                    <PrimaryButton id="ifms-lookup-ok" skipTableCheck size="small" onClick={() => handleOk(selectedIndex)}>Ok</PrimaryButton>&nbsp;
                    <SecondaryButton id="ifms-lookup-cancel" skipTableCheck size="small" onClick={handleCancel}>Cancel</SecondaryButton>
                  </div>
                </div>
                <div className="clearfix"></div>
              </div>
            )}
        </LoopupPopup>
      }
    </>
  );
}

LookupField.defaultProps = {
  placeholder: '',
  onSelect: () => { },
  onTextChange: () => { },
  filter: () => { },
  disabled: false,
  searchMatchOnTab: false,
  triggerLookupOnTab: false,
  focusableWhenDisabled: false,
  showEmptyTable: true,
  isMultiple: false,
  noBorder: false,
  noRightBorder: false,
  noLeftBorder: false,
  isMultipleSelected: false,
  isMultiRptOption: false,
};

LookupField.propTypes = {
  placeholder: PropTypes.string.isRequired,
  query: PropTypes.func.isRequired,
  width: PropTypes.string,
  columns: PropTypes.array.isRequired,
  onSelect: PropTypes.func.isRequired,
  onTextChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onCancel: PropTypes.func.isRequired,
  labelProp: PropTypes.string.isRequired,
  initialValue: PropTypes.string,
  filter: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  lookupId: PropTypes.string,
  onTabNext: PropTypes.func,
  onTabPrevious: PropTypes.func,
  searchMatchOnTab: PropTypes.bool.isRequired,
  activator: PropTypes.func,
  hasLookupValue: PropTypes.bool,
  triggerLookupOnTab: PropTypes.bool.isRequired,
  focusableWhenDisabled: PropTypes.bool.isRequired,
  backgroundColor: PropTypes.string,
  maxLength: PropTypes.number,
  showEmptyTable: PropTypes.bool,
  isMultiple: PropTypes.bool,
  noBorder: PropTypes.bool,
  tableHeight: PropTypes.string,
  noRightBorder: PropTypes.bool,
  noLeftBorder: PropTypes.bool,
  isLookupTable: PropTypes.bool,
  isMultipleSelected: PropTypes.bool,
  isMultiRptOption: PropTypes.bool,
};

export default React.memo(LookupField);